import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  userRole: 'Admin' | 'Student' | 'Staff' | null;
  loading: boolean;
  authMethod: 'cognito' | 'entra' | null;
  entraUser: any | null;
  login: (email: string, role: 'Admin' | 'Student', tokens: { idToken: string; accessToken: string }) => void;
  loginWithEntra: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'Admin' | 'Student' | 'Staff' | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState<'cognito' | 'entra' | null>(null);
  const [entraUser, setEntraUser] = useState<any | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check Cognito authentication
        const token = sessionStorage.getItem('idToken');
        const email = sessionStorage.getItem('userEmail');
        const role = sessionStorage.getItem('userRole') as 'Admin' | 'Student' | null;
        
        if (token && email && role) {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            try {
              const payload = JSON.parse(atob(tokenParts[1]));
              const now = Math.floor(Date.now() / 1000);
              
              if (payload.exp && payload.exp > now) {
                setIsAuthenticated(true);
                setUserEmail(email);
                setUserRole(role);
                setAuthMethod('cognito');
              } else {
                sessionStorage.clear();
              }
            } catch (decodeError) {
              sessionStorage.clear();
            }
          } else {
            sessionStorage.clear();
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        sessionStorage.clear();
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  const loginWithEntra = async () => {
    throw new Error('Entra ID authentication not available. Please install @azure/msal-browser and @azure/msal-react packages and run setup-azure.sh');
  };

  const login = (email: string, role: 'Admin' | 'Student', tokens: { idToken: string; accessToken: string }) => {
    sessionStorage.setItem('idToken', tokens.idToken);
    sessionStorage.setItem('accessToken', tokens.accessToken);
    sessionStorage.setItem('userEmail', email);
    sessionStorage.setItem('userRole', role);
    
    setIsAuthenticated(true);
    setUserEmail(email);
    setUserRole(role);
    setAuthMethod('cognito');
    setEntraUser(null);
  };

  const logout = async () => {
    try {
      sessionStorage.clear();
      
      if (typeof window !== 'undefined') {
        const forms = document.querySelectorAll('form');
        forms.forEach(form => {
          if (form instanceof HTMLFormElement) {
            form.reset();
          }
        });
      }
      
      setIsAuthenticated(false);
      setUserEmail(null);
      setUserRole(null);
      setAuthMethod(null);
      setEntraUser(null);
      
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.reload();
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userEmail,
      userRole,
      loading,
      authMethod,
      entraUser,
      login,
      loginWithEntra,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
